// 🎟️ My Requests — the rules (My Requests revamp, founder picks).
//
// Pure functions, no React: what a row IS (open / terminal / countered),
// which block it sits in (Tonight / Earlier), what its pill says, and which
// chip — if any — it may carry. Every screen falls out of these so nothing is
// decided case by case (the founder-approved rules table, round 2 §C).
//
//   • Tonight = the whole night: everything made since 6am today (or since
//     6am yesterday if it's before 6am now), whatever its state, PLUS anything
//     still open. Played / declined rows from tonight stay in Tonight — rows
//     never jump below the line while the fan is looking at them.
//   • Earlier = everything else. Final pills only, never a chip.
//   • Newest first inside a block; a live counter-offer jumps the line (the
//     DJ is waiting on the fan's answer — it's their money).
//   • Chips only when they can work: Boost needs an open, waiting row whose
//     DJ is currently taking requests; Try again needs a failed payment from
//     tonight; Respond needs a live counter.
//   • closesAt (server, lib/pendingWindow) says when an open request stops
//     waiting on the DJ. The pill NEVER derives from time — only from status —
//     so a row can't claim "Not charged" before the server actually released
//     the hold; time only decides grouping and chips.
import { boostNudgeBelowFor } from "../utils/payinConfig";
import { formatLocale } from "../i18n";
import type { FanClaim } from "../types/claims";

export type FanOrderStatus =
  | "pending"
  | "accepted"
  | "queued"
  | "played"
  | "declined"
  | "frozen"
  | "expired"
  | "refunded"
  | string;

// One shape for every fan-facing order read — server lib/fanOrderRow.js.
// Older servers omit the newer fields; every reader treats them as optional.
export type FanOrderRow = {
  orderId: string;
  status: FanOrderStatus;
  paid: boolean;
  searchQuery: string;
  amount?: number | null;
  originalAmount?: number | null;
  increaseCount?: number;
  currency?: string;
  tier?: "standard" | "priority" | "fast" | null;
  isPriority?: boolean;
  trackImage?: string | null;
  // Setlist rows: first 4 songs' art (server setlistArt) — cover mosaic.
  trackImages?: string[] | null;
  trackArtists?: string[];
  trackAlbum?: string;
  djSlug?: string | null;
  djName?: string | null;
  djAvatarUrl?: string | null;
  djCanTakePayments?: boolean;
  djCapsLifted?: boolean;
  /** The DJ's "taking requests" switch — Boost only shows while it's on. */
  djAccepting?: boolean;
  declineReason?: string | null;
  counterAmount?: number | null;
  createdAt?: string | null;
  paidAt?: string | null;
  seenByDjAt?: string | null;
  hasEmail?: boolean;
  /** When an OPEN request stops waiting on the DJ (ISO), null when terminal. */
  closesAt?: string | null;
  // ⭐ FAN BOOST — the fan spent points on this one (gold row treatment).
  fanBoost?: boolean;
  isPlaylist?: boolean;
  receiptId?: string | null;
  playlistDeliveredAt?: string | null;
  // "Wasn't played?" (founder, 2026-10-09): may the fan still say this played
  // request never played, and their claim once they have. Older servers omit
  // both — no claim door, same as before.
  claimable?: boolean;
  claim?: FanClaim | null;
};

export const MAX_MONEY_BOOSTS = 3;

export const counterLive = (r: FanOrderRow): boolean =>
  r.status === "declined" &&
  r.declineReason === "more_money" &&
  typeof r.counterAmount === "number" &&
  r.counterAmount > 0;

/** Nothing can change any more (a plain decline included; a live counter is NOT terminal). */
export const isTerminal = (r: FanOrderRow): boolean =>
  ["played", "refunded", "expired", "frozen"].includes(r.status) ||
  (r.status === "declined" && !counterLive(r));

export const isAcceptedLike = (r: FanOrderRow): boolean =>
  ["accepted", "queued", "played"].includes(r.status);

const ts = (iso?: string | null): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return isNaN(t) ? null : t;
};

/** Still waiting on the DJ AND inside its window (server closesAt; no closesAt = older server = trust status). */
export const isOpen = (r: FanOrderRow, now = Date.now()): boolean => {
  if (isTerminal(r)) return false;
  const closes = ts(r.closesAt);
  return closes === null ? true : closes > now;
};

/** 6am local today — or 6am yesterday when it's still "tonight" (before 6am). */
export const nightStart = (now = Date.now()): number => {
  const d = new Date(now);
  d.setHours(6, 0, 0, 0);
  if (d.getTime() > now) d.setDate(d.getDate() - 1);
  return d.getTime();
};

export const isFromTonight = (r: FanOrderRow, now = Date.now()): boolean => {
  const made = ts(r.createdAt);
  return made !== null && made >= nightStart(now);
};

export type FanRowGroups = {
  tonight: FanOrderRow[];
  earlier: FanOrderRow[];
  /** "Tonight" while the block holds tonight's rows; "Last night" when it only
   *  holds still-open rows from before 6am; null when the block is empty. */
  tonightLabel: "Tonight" | "Last night" | null;
};

export function groupRows(rows: FanOrderRow[], now = Date.now()): FanRowGroups {
  const tonight: FanOrderRow[] = [];
  const earlier: FanOrderRow[] = [];
  for (const r of rows) {
    if (isFromTonight(r, now) || isOpen(r, now)) tonight.push(r);
    else earlier.push(r);
  }
  const newestFirst = (a: FanOrderRow, b: FanOrderRow) =>
    (ts(b.createdAt) || 0) - (ts(a.createdAt) || 0);
  tonight.sort(newestFirst);
  earlier.sort(newestFirst);
  // Money questions jump the line — live counters float to the top, keeping
  // their own newest-first order among themselves.
  const counters = tonight.filter(counterLive);
  const rest = tonight.filter((r) => !counterLive(r));
  const ordered = [...counters, ...rest];
  const tonightLabel = !ordered.length
    ? null
    : ordered.some((r) => isFromTonight(r, now))
    ? "Tonight"
    : "Last night";
  return { tonight: ordered, earlier, tonightLabel };
}

/* ── pills ─────────────────────────────────────────────────────────────── */

export type PillKind =
  | "waiting"
  | "seen"
  | "boosted"
  | "counter"
  | "accepted"
  | "played"
  | "checking"
  | "declined"
  | "notcharged"
  | "noreply"
  | "refunded"
  | "failed";

/** The fan said this played request never played and the talent hasn't answered yet. */
export const claimOpen = (r: FanOrderRow): boolean => r.status === "played" && r.claim?.status === "open";

/** Status → the one word the fan sees. Time never enters here (see header). */
export function pillFor(r: FanOrderRow): PillKind {
  if (counterLive(r)) return "counter";
  // "Wasn't played?" pending: Checking, not Played (founder, 2026-10-09). A
  // claim that ends in a refund flips the status itself to refunded.
  if (claimOpen(r)) return "checking";
  if (r.status === "played") return "played";
  if (r.status === "accepted" || r.status === "queued") return "accepted";
  if (r.status === "pending" && r.fanBoost) return "boosted";
  if (r.status === "pending" && r.seenByDjAt) return "seen";
  if (r.status === "pending") return "waiting";
  if (r.status === "declined") return "declined";
  // Hold released, never charged — for a free request there was no hold to
  // release, so the honest word is simply that nobody answered.
  if (r.status === "expired") return typeof r.amount === "number" && r.amount > 0 ? "notcharged" : "noreply";
  if (r.status === "refunded") return "refunded";
  if (r.status === "frozen") return "failed";
  return "declined";
}

/* ── the four tabs on My Profile (founder, 2026-11-02) ──────────────
   All | Played | Declined | Playlists. A playlist is always under Playlists.
   A request is Played once it was played (a "Wasn't played?" claim still
   waiting too); Declined holds everything that ended without playing:
   declined, no reply, not charged, refunded, payment failed. One still under
   way (waiting, seen, boosted, accepted, a new price) is in All only. The row's
   own pill keeps saying exactly what happened. */

export type FanTab = "all" | "played" | "declined" | "playlists";

export function tabFor(r: FanOrderRow): Exclude<FanTab, "all"> | null {
  if (r.isPlaylist) return "playlists";
  switch (pillFor(r)) {
    case "played":
    case "checking":
      return "played";
    case "declined":
    case "notcharged":
    case "noreply":
    case "refunded":
    case "failed":
      return "declined";
    default:
      return null;
  }
}

export const inTab = (r: FanOrderRow, tab: FanTab): boolean => tab === "all" || tabFor(r) === tab;

/* ── chips (the mini slabs on a compact row) ───────────────────────────── */

export type ChipKind = "boost" | "respond" | "retry";

export type ChipContext = {
  signedIn: boolean;
  /** Spendable points balance (signed-in fans only). */
  points: number;
  /** ⭐ price of a points boost — server knob, 300 on older servers. */
  boostCost: number;
  now?: number;
};

/** Money boost door: same rule the confirmation page used (pending/queued,
 *  under the 3-boost cap, never on a live counter; a free request only when
 *  the server says the DJ can take payments). */
export const canMoneyBoost = (r: FanOrderRow): boolean =>
  !counterLive(r) &&
  (r.status === "pending" || r.status === "queued") &&
  Number(r.increaseCount || 0) < MAX_MONEY_BOOSTS &&
  (typeof r.amount === "number" && r.amount > 0 ? true : r.djCanTakePayments === true);

/** Points door (redemption v1 rules): pending, not yet boosted, under
 *  the per-currency nudge ladder, and the fan can afford it. */
export const canPointsBoost = (r: FanOrderRow, ctx: ChipContext): boolean =>
  ctx.signedIn &&
  r.status === "pending" &&
  !r.fanBoost &&
  typeof r.amount === "number" &&
  r.amount < boostNudgeBelowFor(r.currency) &&
  ctx.points >= ctx.boostCost;

export function chipFor(r: FanOrderRow, ctx: ChipContext, inTonight: boolean): ChipKind | null {
  if (counterLive(r)) return "respond";
  if (!inTonight) return null;
  const now = ctx.now ?? Date.now();
  if (r.status === "frozen") return "retry";
  if (!isOpen(r, now)) return null;
  // Boost only when someone's looking: the DJ's switch is on (older servers
  // omit the field → treat as on, same as before the field existed).
  if (r.djAccepting === false) return null;
  if (canMoneyBoost(r) || canPointsBoost(r, ctx)) return "boost";
  return null;
}

/* ── which card is open on arrival ─────────────────────────────────────── */

/** One card open: the one just paid for; else the top live counter; else the
 *  newest open row in Tonight. A live counter always wins over the landing
 *  row (the landing row keeps a green ring instead). */
export function expandedOnLoad(tonight: FanOrderRow[], landingId: string | null): string | null {
  const counter = tonight.find(counterLive);
  if (counter) return counter.orderId;
  if (landingId && tonight.some((r) => r.orderId === landingId)) return landingId;
  const open = tonight.find((r) => isOpen(r));
  return open ? open.orderId : null;
}

/** "tonight 11:42 PM" energy without the wordiness: time for today, short
 *  date for anything older. */
export const whenLabel = (iso?: string | null, now = Date.now()): string => {
  const t = ts(iso);
  if (t === null) return "";
  const d = new Date(t);
  if (now - t < 60_000) return "just now";
  // Same NIGHT (since 6am), not same calendar day — a 11:30 PM request still
  // reads "11:30 PM" at 1 AM instead of flipping to yesterday's date.
  const sameNight = t >= nightStart(now) && t <= now;
  return sameNight
    ? d.toLocaleTimeString(formatLocale(), { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString(formatLocale(), { month: "short", day: "numeric" });
};

/** Does anything on the page still need polling? */
export const anyLive = (rows: FanOrderRow[]): boolean => rows.some((r) => !isTerminal(r));
